import { isNullOrUndefined } from "./ValidationHelper";

export type IUrlFormatProp = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
};

export const updateUrl = (pathname: string) => {
    window.history.replaceState({ ...window.history.state, path: pathname }, document.title, pathname);
};

// Removes all query parameters
export const refineURL = () => updateUrl(window.location.pathname);

export const getFromUrl = (paramName: string): string | null => {
    if (paramName === "") {
        return null;
    }

    return new URL(window.location.href).searchParams.get(paramName);
};

export const existInUrl = (paramName: string): boolean => {
    if (paramName === "") {
        return false;
    }

    return new URL(window.location.href).searchParams.has(paramName);
};

export const removeFromUrl = (paramName: string): void => {
    if (paramName === "") {
        return;
    }

    const currentUrl = new URL(window.location.href);

    if (currentUrl.searchParams.get(paramName) !== null) {
        currentUrl.searchParams.delete(paramName);
    }

    const finalUrl = currentUrl.pathname + currentUrl.search;

    window.history.replaceState({ ...window.history.state, path: finalUrl }, document.title, finalUrl);
};

export const camelCase = (value: string): string => value[0].toLowerCase() + value.substr(1);

const urlParamsPattern = /%7B(.*?)%7D/gi;

export function urlFormat<T extends object>(
    url: TypedUrl<T>,
    params: T & IUrlFormatProp,
    addQuery?: boolean,
    removeEmptyParams?: boolean,
): string;
export function urlFormat(
    url: string,
    params?: IUrlFormatProp,
    addQuery?: boolean,
    removeEmptyParams?: boolean,
): string;
export function urlFormat<T>(
    url?: string | TypedUrl<T>,
    params?: IUrlFormatProp,
    addQuery?: boolean,
    removeEmptyParams?: boolean,
): string {
    if (typeof url === "object") {
        url = url.url;
    }

    if (!url) {
        return "";
    }

    if (!params) {
        return url;
    }

    const obj = { ...params };


    const matches = [];
    let match = urlParamsPattern.exec(url);
    while (match !== null) {
        matches.push(match[1]);
        match = urlParamsPattern.exec(url);
    }

    for (let i = 0; i < matches.length; i += 1) {
        const m = matches[i];
        const propValue: string = camelCase(m);

        let valueTransformed: string = "";

        if (typeof obj[propValue] === "function") {
            valueTransformed = obj[propValue](propValue);
        } else {
            valueTransformed = isNullOrUndefined(obj[propValue]) ? "" : obj[propValue];
        }

        url = url.replace(new RegExp(`/(%7B${m}%7D)`, "gi"), valueTransformed ? "/" + valueTransformed : "");
        url = url.replace(new RegExp(`=(%7B${m}%7D)`, "gi"), "=" + valueTransformed);
        delete obj[propValue];
    }

    if (removeEmptyParams) {
        url = url.replace(/&?\w+=&?(?=&|$)|\?$/gi, "");
    }

    if (addQuery !== true) {
        return url;
    }

    const objRes = Object.keys(obj);

    if (objRes.length > 0) {
        if (url.indexOf("?") < 0) {
            url += "?";
        }

        const searchParams = new URLSearchParams();

        for (let i = 0; i < objRes.length; i += 1) {
            const key = objRes[i];
            const val = obj[key];

            if (!isNullOrUndefined(val)) {
                searchParams.set(key, val);
            }
        }

        url += searchParams.toString();
    }

    return url;
}

export const redirectTo = (url: string, props?: object, newWindow?: boolean): void => {
    const formattedUrl = urlFormat(url, props);

    if (!formattedUrl) {
        return;
    }

    if (newWindow === true) {
        window.open(formattedUrl);
    } else {
        window.location.href = formattedUrl;
    }
};

export const getHostName = (url: string): string =>
    url ? new URL(url, document.location.href).hostname : "";

export const removeHost = (url: string): string => url.replace(document.location.origin, "");

export const hostName = getHostName(typeof window === "object" ? window.location.href : "");
